import { supabase } from './supabase';

export type SocialConnectionUsage = {
  active_count: number;
  purchased_quantity: number;
  grandfathered_quantity: number;
  total_capacity: number;
  can_add: boolean;
};

export const SOCIAL_CONNECTION_PRICE_LABEL =
  (typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_SOCIAL_CONNECTION_PRICE_LABEL) ||
  '$29/mo';

export async function getSocialConnectionUsage(
  businessId: string,
): Promise<SocialConnectionUsage | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('get_social_connection_usage', {
    p_business_id: businessId,
  });
  if (error) {
    console.warn('get_social_connection_usage:', error.message);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) {
    return {
      active_count: 0,
      purchased_quantity: 0,
      grandfathered_quantity: 0,
      total_capacity: 0,
      can_add: false,
    };
  }
  return {
    active_count: Number(row.active_count) || 0,
    purchased_quantity: Number(row.purchased_quantity) || 0,
    grandfathered_quantity: Number(row.grandfathered_quantity) || 0,
    total_capacity: Number(row.total_capacity) || 0,
    can_add: row.can_add === true,
  };
}

/**
 * Ensures the business has capacity for one more social connection.
 * If not, purchases one seat (owner-only Stripe action).
 */
export async function ensureSocialConnectionSeat(
  businessId: string,
): Promise<{ success: true; purchased: boolean } | { success: false; error: string }> {
  if (!supabase) {
    return { success: false, error: 'App not initialized' };
  }

  const usage = await getSocialConnectionUsage(businessId);
  if (usage?.can_add) {
    return { success: true, purchased: false };
  }

  const { data, error } = await supabase.functions.invoke('stripe-subscription', {
    body: { action: 'add-social-connection-seat', businessId },
  });
  if (error) {
    return { success: false, error: error.message || 'Request failed' };
  }
  if (data && typeof data.error === 'string') {
    return { success: false, error: data.error };
  }
  if (!data?.success) {
    return { success: false, error: 'Unexpected response from server' };
  }
  return { success: true, purchased: true };
}
